import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '../fetch';
import * as Hub from '../ogm-hub';

export function productHeaderPriceId(productHeader: Hub.ProductHeaderPrices) {
	return `${productHeader.corpSupplierCode}|${productHeader.productNumber}`;
}

export type IProductHeaderPricesState = EntityState<Hub.ProductHeaderPrices>;

export const productHeaderPricesAdapter =
	createEntityAdapter<Hub.ProductHeaderPrices>({
		selectId: productHeaderPriceId,
	});

export const fetchHeaderLevelPrices = createAsyncThunk(
	'productHeaderPrices/fetchHeaderLevelPrices',
	async (params: Hub.SupplierProductIdentifier[], { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				Hub.PriceController.GetProductHeaderPricesAcrossSuppliers(params)
			);
			return result.prices;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface IFetchHeaderLevelBlueprintPricesParameters {
	blueprintId: number;
	productNumbers: string[];
}

export const fetchHeaderLevelBlueprintPrices = createAsyncThunk(
	'productHeaderPrices/fetchHeaderLevelBlueprintPrices',
	async (
		params: IFetchHeaderLevelBlueprintPricesParameters,
		{ rejectWithValue }
	) => {
		try {
			const productNumbers = params.productNumbers.map((p) =>
				p.substring(p.indexOf('|') + 1, p.length)
			);

			const result = await unwrapFetch2(
				Hub.PriceController.GetProductHeaderPrices(
					params.blueprintId,
					productNumbers
				)
			);
			return result.prices;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const productHeaderPricesSlice = createSlice({
	name: 'productHeaderPrices',
	initialState: productHeaderPricesAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchHeaderLevelPrices.fulfilled, (state, action) => {
			productHeaderPricesAdapter.upsertMany(state, action.payload);
		});
		builder.addCase(
			fetchHeaderLevelBlueprintPrices.fulfilled,
			(state, action) => {
				productHeaderPricesAdapter.upsertMany(state, action.payload);
			}
		);
	},
});

export function selectProductHeaderPrice(state, id: string) {
	return state.productHeaderPrices.entities[id];
}
